/**
 * PurchaseRequest.js
 *
 * Flow:
 *  1. User searches for an approved Sales Order by SO number.
 *  2. Matching SOs appear as cards; user clicks "Raise PR" on one.
 *  3. SO's existing line items are shown in a table.
 *     User selects which items to include (checkbox) and sets purchase qty.
 *  4. Submit calls POST /api/purchase-requests.
 *     requestRaisedBy is auto-captured from the token on the backend.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSearch, faSpinner, faFileContract,
    faBuilding, faUser, faTimes, faShoppingCart,
    faCheckCircle, faPlus, faChevronLeft,
    faExclamationTriangle, faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons'
import { createPurchaseRequest, searchSalesOrders, updateSalesOrder } from './purchaseRequestService'
import PageHeader from '../utils/PageHeader'
import '../../css/purchase.css'
import '../../css/common.css'

/* ─── Helpers ─────────────────────────────────────────────── */
const formatDate = (d) =>
    d
        ? new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
        : '—'

const getModelName = (li) =>
    li?.model?.modelName ?? li?.model?.name ?? li?.modelName ?? li?.name ?? '—'

const getPartCode = (li) =>
    li?.model?.partCode ?? li?.partCode ?? '—'

/* Available qty = SO qty – already purchased qty */
const getPendingQty = (li) =>
    Math.max(0, Number(li?.quantity ?? 0) - Number(li?.purchasedQuantity ?? 0))

const getLineId = (li, idx) => li?._id ?? li?.id ?? `line-${idx}`

/* ══════════════════════════════════════════════════════════
   SODetailView — shows line items from a selected SO
   and lets the user choose which to include in the PR.
══════════════════════════════════════════════════════════ */
const SODetailView = ({ so, onBack, onSuccess }) => {
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')

    /* Build initial selection state from SO line items */
    const buildInit = useCallback(() => {
        const qty = {}
        const included = {}
        const spec = {}                          // ← add this
            ; (so?.lineItems ?? []).forEach((li, idx) => {
                const id = getLineId(li, idx)
                const avail = getPendingQty(li)
                qty[id] = avail
                included[id] = avail > 0
                spec[id] = ''
            })
        return { qty, included, spec }
    }, [so])

    const [sel, setSel] = useState(buildInit)
    const [submitting, setSub] = useState(false)
    const [done, setDone] = useState(false)
    const [submitErr, setErr] = useState('')
    const [createdPR, setPR] = useState(null)
    const [isOem, setIsOem] = useState(false)

    const lineItems = Array.isArray(so?.lineItems) ? so.lineItems : []

    const toggle = (id) =>
        setSel((p) => ({ ...p, included: { ...p.included, [id]: !p.included[id] } }))

    const changeQty = (id, val, max) => {
        const n = Math.min(Math.max(0, Number(val || 0)), max)
        setSel((p) => ({ ...p, qty: { ...p.qty, [id]: n } }))
    }

    const changeSpec = (id, val) =>
        setSel((p) => ({ ...p, spec: { ...p.spec, [id]: val } }))

    /* ── Submit ── */
    const handleSubmit = async () => {
        setErr('')
        const selected = lineItems
            .filter((li, idx) => {
                const id = getLineId(li, idx)
                return sel.included[id] && Number(sel.qty[id] ?? 0) > 0
            })
            .map((li, idx) => {
                const id = getLineId(li, idx)
                return {
                    model: li?.model?._id ?? li?.model ?? null,
                    quantity: Number(sel.qty[id]),
                    specification: sel.spec[id]?.trim() || null,
                    ...(li?.committedDeliveryDate
                        ? { requiredDeliveryDate: li.committedDeliveryDate }
                        : {}),
                    ...(li?.committedDeliveryDate
                        ? { lastDateOfOrderDelivery: li.committedDeliveryDate }
                        : {}),
                }
            })

        if (selected.length === 0) {
            setErr('Please select at least one line item with quantity > 0.')
            return
        }

        setSub(true)
        try {
            const actualSoLineItems = Array.isArray(so?.lineItems) ? so.lineItems : []
            const updatedSoLineItems = actualSoLineItems.map((orig) => {
                const origModelId = String(orig?.model?._id || orig?.model || '')
                const matched = selected.find((d) => String(d.model || '') === origModelId)

                if (!matched) return orig

                const currentPQty = Number(orig.purchasedQuantity || 0)
                const addedQty = Number(matched.quantity || 0)
                const soQty = Number(orig.quantity || 0)
                const newPQty = Math.min(currentPQty + addedQty, soQty)

                return { ...orig, purchasedQuantity: newPQty }
            })

            await updateSalesOrder(token, so._id, {
                lineItems: updatedSoLineItems,
                status: so.status ?? 'pending',
            })

            const result = await createPurchaseRequest(token, {
                salesOrder: so._id,
                lineItems: selected,
                isOem: isOem
            })
            const newPR = result?.data ?? result
            setPR(newPR)
            setDone(true)
        } catch (e) {
            console.error(e)
            setErr(e.message || 'Failed to create purchase request. Please try again.')
        } finally {
            setSub(false)
        }
    }

    /* ── Success screen ── */
    if (done) {
        return (
            <div className='text-center py-14 bg-green-50 border border-green-200 rounded-xl mt-4 px-6'>
                <FontAwesomeIcon
                    icon={faCheckCircle}
                    className='text-green-500 mb-4'
                    size='4x'
                />
                <h4 className='text-green-800 font-bold mb-2'>
                    Purchase Request Created!
                </h4>
                {createdPR?.prId && (
                    <p className='text-green-700 text-sm mb-1'>
                        PR ID: <strong>{createdPR.prId}</strong>
                    </p>
                )}
                <p className='text-gray-500 text-sm mb-6'>
                    Raised against <strong>{so.salesOrderNumber}</strong> and submitted for approval.
                </p>
                <button
                    className='btn add-btn text-white px-5 font-bold'
                    onClick={onSuccess}
                >
                    Raise Another Request
                </button>
            </div>
        )
    }

    /* ── Detail view ── */
    return (
        <div>
            {/* Back */}
            <button
                onClick={onBack}
                className='btn btn-light border mb-4 text-sm font-semibold flex items-center gap-2'
            >
                <FontAwesomeIcon icon={faChevronLeft} /> Back to Search
            </button>

            {/* SO summary strip */}
            <div className='flex items-center gap-3 mb-4'>
                <div className='w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0'>
                    <FontAwesomeIcon icon={faShoppingCart} className='text-purple-600' />
                </div>
                <div className='flex-1 min-w-0'>
                    <span className='font-bold text-gray-800 text-base'>
                        {so.salesOrderNumber}
                    </span>
                    <span className='ml-2 text-xs text-gray-500'>— Supply Purchase</span>
                </div>
                {so.status && (
                    <span className='badge bg-success text-white'>{so.status}</span>
                )}
            </div>

            {/* SO info card */}
            <div className='bg-white border border-gray-200 rounded-xl p-4 mb-4'>
                <h6 className='font-bold text-gray-700 mb-3 pb-2 border-b border-gray-100'>
                    Sales Order Details
                </h6>
                <div className='grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3'>
                    {[
                        ['Customer', so?.lead?.client?.companyName],
                        ['Lead ID', so?.lead?.leadId],
                        ['Sales Executive', so?.lead?.leadOwner?.name],
                        ['Business Line', so?.businessLine?.name],
                        ['Customer PO No.', so?.customerPONumber],
                        ['Date of SO', formatDate(so?.dateOfSO || so?.createdAt)],
                    ].map(([label, val]) => (
                        <div key={label}>
                            <div className='text-xs text-gray-400 font-semibold mb-0.5 uppercase tracking-wide'>
                                {label}
                            </div>
                            <div className='text-sm font-bold text-gray-800'>{val || '—'}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Line items selectable table */}
            <div className='bg-white border border-gray-200 rounded-xl p-4 mb-4'>
                <div className='flex justify-between items-center mb-3 pb-2 border-b border-gray-100 flex-wrap gap-2'>
                    <h6 className='font-bold text-gray-700 mb-0'>
                        Line Items
                        <span className='ml-2 text-xs text-gray-400 font-normal'>
                            Select items to include in this Purchase Request
                        </span>
                    </h6>
                    <label className='inline-flex items-center cursor-pointer mb-0'>
                        <input
                            type='checkbox'
                            className='sr-only peer'
                            checked={isOem}
                            onChange={(e) => setIsOem(e.target.checked)}
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        <span className='ms-3 text-sm font-bold text-gray-800'>
                            Is OEM Order
                        </span>
                    </label>
                </div>

                <div className='table-responsive'>
                    <table className='table align-middle table-nowrap mb-0 so-table'>
                        <thead className='table-light'>
                            <tr>
                                <th className='text-center'>Include</th>
                                <th>SI</th>
                                <th>Model</th>
                                <th>Specification</th>
                                <th>Part Code</th>
                                <th className='text-center'>SO Qty</th>
                                <th className='text-center'>Purchased</th>
                                <th className='text-center'>Pending</th>
                                <th>Purchase Qty</th>
                                <th>Delivery Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length > 0 ? (
                                lineItems.map((li, idx) => {
                                    const id = getLineId(li, idx)
                                    const avail = getPendingQty(li)
                                    const checked = !!sel.included[id]
                                    const qty = Number(sel.qty[id] ?? 0)

                                    const qtyOptions = Array.from(
                                        { length: avail + 1 },
                                        (_, i) => i
                                    )

                                    return (
                                        <tr
                                            key={id}
                                            className={checked ? '' : 'opacity-40'}
                                        >
                                            {/* Checkbox — plain input avoids the global form-check-input override */}
                                            <td className='text-center'>
                                                <input
                                                    type='checkbox'
                                                    checked={checked}
                                                    onChange={() => toggle(id)}
                                                    disabled={avail === 0}
                                                    className='w-4 h-4 accent-purple-600 cursor-pointer disabled:cursor-not-allowed'
                                                />
                                            </td>

                                            <td className='text-gray-800 font-semibold'>{idx + 1}</td>

                                            <td className='text-gray-800 font-semibold'>
                                                <div>{getModelName(li)}</div>  {/* wrap in div to push spans below */}
                                                <div className='flex flex-wrap gap-1 mt-1'>
                                                    {
                                                        li?.storeWiseQuantity?.map((data, idx) => (
                                                            <span className='text-xs font-normal text-red-500' key={idx}>
                                                                {data?.storeName}: {data?.availableQuantity}
                                                            </span>
                                                        ))
                                                    }
                                                </div>
                                            </td>

                                            <td>
                                                <input
                                                    type='text'
                                                    className='form-control form-control-sm'
                                                    placeholder='Enter specification…'
                                                    value={sel.spec[id] ?? ''}
                                                    onChange={(e) => changeSpec(id, e.target.value)}
                                                    disabled={!checked || avail === 0}
                                                    style={{ minWidth: '160px' }}
                                                />
                                            </td>

                                            <td className='text-gray-700 text-sm'>
                                                {getPartCode(li)}
                                            </td>

                                            <td className='text-center text-gray-800 font-semibold'>
                                                {li.quantity ?? '—'}
                                            </td>

                                            <td className='text-center text-gray-800'>
                                                {Number(li.purchasedQuantity ?? 0)}
                                            </td>

                                            <td className='text-center'>
                                                <span
                                                    className={
                                                        avail === 0
                                                            ? 'text-red-700 font-bold'
                                                            : 'text-green-700 font-bold'
                                                    }
                                                >
                                                    {avail}
                                                </span>
                                            </td>

                                            {/* Purchase qty — always show select (like CreateDI) */}
                                            <td>
                                                <select
                                                    className='form-select form-select-sm'
                                                    value={qty}
                                                    onChange={(e) =>
                                                        changeQty(id, e.target.value, avail)
                                                    }
                                                    disabled={!checked || avail === 0}
                                                >
                                                    {avail === 0 ? (
                                                        <option value={0}>0 — Fully dispatched</option>
                                                    ) : (
                                                        qtyOptions.map((n) => (
                                                            <option key={n} value={n}>
                                                                {n}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                                <div className='text-xs text-gray-600 mt-1'>
                                                    Purchased: {Number(li.purchasedQuantity ?? 0)} /{' '}
                                                    {Number(li.quantity ?? 0)} | Pending: {avail}
                                                </div>
                                            </td>

                                            <td>
                                                <div className='flex items-center gap-1 text-xs text-gray-700'>
                                                    <FontAwesomeIcon
                                                        icon={faCalendarAlt}
                                                        className='text-gray-500'
                                                    />
                                                    {formatDate(li.committedDeliveryDate)}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className='text-center py-6 text-gray-400 text-sm'
                                    >
                                        No line items found in this Sales Order.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Error */}
            {submitErr && (
                <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {submitErr}
                </div>
            )}

            {/* Submit row */}
            <div className='flex justify-end mt-4'>
                <button
                    id='btn-create-pr'
                    className='btn add-btn text-white px-5 flex items-center gap-2 font-bold'
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Creating…
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faPlus} /> Create Purchase Request
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

/* ══════════════════════════════════════════════════════════
   Main PurchaseRequest page — SO search step
══════════════════════════════════════════════════════════ */
const PurchaseRequest = () => {
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')
    const userDetails = useSelector((store) => store.user)
    const navigate = useNavigate()

    const [search, setSearch] = useState('')
    const [debouncedSearch] = useDebounce(search, 500)
    const [soList, setSoList] = useState([])
    const [selectedSO, setSelectedSO] = useState(null)
    const [loading, setLoading] = useState(false)
    const [searched, setSearched] = useState(false)

    /* ── Fetch SOs matching the search query ── */
    const fetchSOs = useCallback(
        async (q) => {
            if (!q) return
            setLoading(true)
            setSearched(true)
            try {
                const json = await searchSalesOrders(token, q, userDetails)
                const docs = json?.data?.docs ?? json?.data ?? (Array.isArray(json) ? json : [])
                setSoList(Array.isArray(docs) ? docs : [])
            } catch (e) {
                console.error(e)
                setSoList([])
            } finally {
                setLoading(false)
            }
        },
        [token, userDetails]
    )

    useEffect(() => {
        if (debouncedSearch.trim()) {
            fetchSOs(debouncedSearch.trim())
        } else {
            setSoList([])
            setSearched(false)
        }
    }, [debouncedSearch, fetchSOs])

    const clearAll = () => {
        setSearch('')
        setSoList([])
        setSearched(false)
        setSelectedSO(null)
    }

    /* ── SO selected → show detail ── */
    if (selectedSO) {
        return (
            <div>
                <PageHeader
                    pageName='CREATE PURCHASE REQUEST'
                    breadcrump1='Home'
                    breadcrump2='Purchase Request'
                    link='/dashboard'
                />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>
                    <SODetailView
                        so={selectedSO}
                        onBack={() => setSelectedSO(null)}
                        onSuccess={clearAll}
                    />
                </div>
            </div>
        )
    }

    /* ── Search page ── */
    return (
        <div>
            <PageHeader
                pageName='CREATE PURCHASE REQUEST'
                breadcrump1='Home'
                breadcrump2='Purchase Request'
                link='/dashboard'
            />

            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>

                {/* Back to list */}
                <button
                    onClick={() => navigate('/view_pr')}
                    className='btn btn-light border mb-4 text-sm font-semibold flex items-center gap-2'
                >
                    <FontAwesomeIcon icon={faChevronLeft} /> View All Requests
                </button>

                {/* Search card */}
                <div className='bg-white rounded-xl p-5 mb-5 shadow-sm border border-gray-200'>
                    <div className='flex items-center gap-3 mb-4'>
                        <div className='w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0'>
                            <FontAwesomeIcon
                                icon={faShoppingCart}
                                className='text-purple-600 text-xl'
                            />
                        </div>
                        <div>
                            <h5 className='text-gray-800 font-bold mb-0'>
                                Raise a Purchase Request
                            </h5>
                            <p className='text-gray-500 text-xs mb-0'>
                                Search for an approved Sales Order by SO number to begin.
                            </p>
                        </div>
                    </div>

                    {/* Search input */}
                    <div className='relative'>
                        <FontAwesomeIcon
                            icon={loading ? faSpinner : faSearch}
                            spin={loading}
                            className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base z-10 pointer-events-none'
                        />
                        <input
                            id='so-search-input'
                            type='text'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder='Search by SO number…'
                            className='purchase-search-input'
                        />
                        {search && (
                            <button
                                onClick={clearAll}
                                className='absolute right-3 top-1/2 -translate-y-1/2 bg-gray-200 hover:bg-gray-300 rounded-full w-7 h-7 flex items-center justify-center text-gray-500 border-0 cursor-pointer transition-all'
                            >
                                <FontAwesomeIcon icon={faTimes} className='text-xs' />
                            </button>
                        )}
                    </div>
                </div>

                {/* Results */}
                {searched && (
                    <div>
                        {/* Result count */}
                        {!loading && (
                            <h6 className='font-semibold text-gray-600 mb-3'>
                                {soList.length === 0
                                    ? `No results for "${debouncedSearch}"`
                                    : `${soList.length} result${soList.length !== 1 ? 's' : ''} for "${debouncedSearch}"`}
                            </h6>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className='text-center py-12 text-gray-400'>
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className='text-3xl mb-3'
                                />
                                <p className='text-sm'>Searching Sales Orders…</p>
                            </div>
                        )}

                        {/* Empty */}
                        {!loading && soList.length === 0 && (
                            <div className='text-center py-14 bg-gray-50 border border-dashed border-gray-300 rounded-xl'>
                                <FontAwesomeIcon
                                    icon={faFileContract}
                                    className='text-gray-300 text-5xl mb-3'
                                />
                                <h5 className='text-gray-500 font-semibold'>
                                    No Sales Orders Found
                                </h5>
                                <p className='text-gray-400 text-sm'>
                                    No matches for "{debouncedSearch}". Try a different SO number.
                                </p>
                            </div>
                        )}

                        {/* SO cards */}
                        {!loading && soList.length > 0 && (
                            <div className='flex flex-col gap-3'>
                                {soList.map((so, idx) => (
                                    <div
                                        key={so._id || idx}
                                        className='purchase-so-card bg-white border border-gray-200 rounded-xl p-4 shadow-sm'
                                    >
                                        <div className='flex items-start gap-3 flex-wrap'>
                                            {/* Icon */}
                                            <div className='w-11 h-11 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5'>
                                                <FontAwesomeIcon
                                                    icon={faFileContract}
                                                    className='text-purple-600 text-lg'
                                                />
                                            </div>

                                            {/* Info */}
                                            <div className='flex-1 min-w-0'>
                                                {/* Title row */}
                                                <div className='flex items-center gap-2 flex-wrap mb-2'>
                                                    <span className='font-bold text-gray-800 text-base'>
                                                        {so.salesOrderNumber ||
                                                            `SO-${so._id?.slice(-6)}`}
                                                    </span>
                                                    {so.status && (
                                                        <span
                                                            className={`badge ${so.status
                                                                .toLowerCase()
                                                                .includes('approved')
                                                                ? 'bg-success'
                                                                : 'bg-warning text-dark'
                                                                }`}
                                                        >
                                                            {so.status}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Key details */}
                                                <div className='grid grid-cols-1 md:grid-cols-3 gap-2 mb-3'>
                                                    {[
                                                        [faBuilding, 'Company', so?.lead?.client?.companyName],
                                                        [faUser, 'Executive', so?.lead?.leadOwner?.name],
                                                        [faFileContract, 'Lead ID', so?.lead?.leadId],
                                                    ].map(([icon, label, val]) => (
                                                        <div key={label}>
                                                            <div className='text-xs text-gray-400 font-semibold'>
                                                                {label}
                                                            </div>
                                                            <div className='text-sm text-gray-700 font-semibold flex items-center gap-1 mt-0.5'>
                                                                <FontAwesomeIcon
                                                                    icon={icon}
                                                                    className='text-gray-400 text-xs'
                                                                />
                                                                {val || '—'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Mini line items preview */}
                                                {Array.isArray(so.lineItems) &&
                                                    so.lineItems.length > 0 && (
                                                        <div className='bg-gray-50 border border-gray-100 rounded-lg overflow-hidden'>
                                                            <table className='w-full text-xs'>
                                                                <thead>
                                                                    <tr className='bg-gray-100'>
                                                                        <th className='text-left px-3 py-2 font-semibold text-gray-600'>
                                                                            Model
                                                                        </th>
                                                                        <th className='text-left px-3 py-2 font-semibold text-gray-600'>
                                                                            Part Code
                                                                        </th>
                                                                        <th className='text-center px-3 py-2 font-semibold text-gray-600'>
                                                                            SO Qty
                                                                        </th>
                                                                        <th className='text-center px-3 py-2 font-semibold text-gray-600'>
                                                                            Pending
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {so.lineItems.map(
                                                                        (li, i) => (
                                                                            <tr
                                                                                key={i}
                                                                                className='border-t border-gray-100'
                                                                            >
                                                                                <td className='px-3 py-2 text-gray-700'>
                                                                                    {getModelName(li)}
                                                                                </td>
                                                                                <td className='px-3 py-2 text-gray-500'>
                                                                                    {getPartCode(li)}
                                                                                </td>
                                                                                <td className='px-3 py-2 text-center text-gray-700'>
                                                                                    {li.quantity}
                                                                                </td>
                                                                                <td className='px-3 py-2 text-center font-bold'>
                                                                                    <span
                                                                                        className={
                                                                                            getPendingQty(li) === 0
                                                                                                ? 'text-red-500'
                                                                                                : 'text-green-600'
                                                                                        }
                                                                                    >
                                                                                        {getPendingQty(li)}
                                                                                    </span>
                                                                                </td>
                                                                            </tr>
                                                                        )
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                            </div>

                                            {/* CTA */}
                                            <div className='flex-shrink-0 self-start'>
                                                <button
                                                    id={`btn-raise-pr-${so._id || idx}`}
                                                    className='btn filter-btn text-white text-sm flex items-center gap-2 font-bold'
                                                    onClick={() => setSelectedSO(so)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    Raise PR
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Idle state */}
                {!searched && !loading && (
                    <div className='text-center py-14 text-gray-400'>
                        <FontAwesomeIcon
                            icon={faSearch}
                            className='text-4xl mb-3 text-gray-300'
                        />
                        <p className='text-sm font-semibold text-gray-500'>
                            Type a Sales Order number to get started
                        </p>
                        <p className='text-xs text-gray-400 mt-1'>
                            Matching approved Sales Orders will appear here
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PurchaseRequest
